import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  TrendingUp, 
  Calendar, 
  Clock, 
  Star,
  ChevronUp,
  ChevronDown,
  Zap,
  Info,
  Check,
  Eye,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { isProfileComplete } from '../utils/profileCompletion';
import { getInitials } from '../utils/avatarUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * VisibilityAndPreviewCard - Consolidated card showing:
 * - Left/Top: Activity score and visibility boost info
 * - Right/Bottom: Profile card preview
 * 
 * Responsive: Side-by-side on desktop, stacked on mobile
 */
/**
 * Returns a contextual tip based on the user's actual activity stats.
 * Tips are prioritized to nudge the most impactful next action.
 */
const getContextualTip = (rankData) => {
  if (!rankData) {
    return "Start a session to build your activity score and climb the rankings!";
  }
  
  const { uniqueLiveDays, totalActiveMinutes, totalReviews } = rankData;
  
  // Priority 1: Never gone live - encourage first session
  if (uniqueLiveDays === 0) {
    return "Your first live day earns +20 pts instantly. Start today!";
  }
  
  // Priority 2: Only 1 day - encourage returning
  if (uniqueLiveDays === 1) {
    return "You've started! Come back another day for +20 more pts. Consistency beats marathon sessions.";
  }
  
  // Priority 3: Low reviews relative to time spent - encourage completing reviews
  if (totalReviews === 0 && totalActiveMinutes > 10) {
    return "Complete reviews during your sessions to earn +5 pts each. Artists remember helpful feedback!";
  }
  
  // Priority 4: Under 3 days - push toward consistency milestone
  if (uniqueLiveDays < 3) {
    return `${3 - uniqueLiveDays} more live day${uniqueLiveDays === 2 ? '' : 's'} to hit 60 pts from consistency alone!`;
  }
  
  // Priority 5: Good consistency but low reviews - balance the approach
  if (totalReviews < uniqueLiveDays * 2) {
    return "Great consistency! Now boost your score faster: each review adds +5 pts.";
  }
  
  // Priority 6: Doing well - encourage maintaining
  if (uniqueLiveDays >= 5) {
    return "You're a top performer! Keep the streak alive - every live day adds +20 pts.";
  }
  
  // Default: General encouragement
  return "Pro tip: Short daily sessions rank higher than occasional long ones.";
};

const VisibilityAndPreviewCard = ({ 
  user, 
  onGoLive,
  avatar,
  displayName,
  username,
  bio,
  cardImagePosition,
  setCardImagePosition
}) => {
  const [rankData, setRankData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    const fetchRankData = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/reviewers/all`);
        const reviewers = response.data || [];
        
        const userIndex = reviewers.findIndex(r => r.id === user?.id || r.id === user?._id);
        const userData = reviewers.find(r => r.id === user?.id || r.id === user?._id);
        
        if (userIndex !== -1 && userData) {
          setRankData({
            rank: userIndex + 1,
            totalVisible: reviewers.length,
            activityScore: userData.activity_score || 0,
            totalActiveMinutes: userData.total_active_minutes || 0,
            uniqueLiveDays: userData.unique_live_days || 0,
            totalReviews: userData.activity_total_reviews || 0,
            isVisible: true
          });
        } else {
          setRankData({
            rank: null,
            totalVisible: reviewers.length,
            activityScore: 0,
            totalActiveMinutes: 0,
            uniqueLiveDays: 0,
            totalReviews: 0,
            isVisible: false
          });
        }
      } catch (error) {
        console.error('Failed to fetch rank data:', error);
        setRankData(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.id || user?._id) {
      fetchRankData();
    } else {
      setLoading(false);
    }
  }, [user]);
  
  const getNextMilestone = () => {
    if (!rankData) return { action: "Start a session to begin ranking", impact: "+20 points", priority: "high" };
    
    const { uniqueLiveDays, totalActiveMinutes } = rankData;
    
    if (uniqueLiveDays === 0) {
      return { action: "Start your first session", impact: "+20 points (1 live day)", priority: "high" };
    }
    if (totalActiveMinutes < 30) {
      return { action: "Stay live for 10+ minutes", impact: "+10 points", priority: "high" };
    }
    if (uniqueLiveDays < 3) {
      return { action: `Be active ${3 - uniqueLiveDays} more day${3 - uniqueLiveDays > 1 ? 's' : ''}`, impact: `+${(3 - uniqueLiveDays) * 20} pts`, priority: "medium" };
    }
    return { action: "Keep reviewing consistently", impact: "+20 pts/day", priority: "low" };
  };
  
  const milestone = getNextMilestone();
  
  const scoreBreakdown = rankData ? {
    minutes: Math.round(rankData.totalActiveMinutes * 1),
    days: rankData.uniqueLiveDays * 20,
    reviews: rankData.totalReviews * 5
  } : { minutes: 0, days: 0, reviews: 0 };
  
  return (
    <Card 
      className="p-4 sm:p-6 mb-6 border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" 
      data-testid="visibility-preview-card"
    >
      {/* Main Container - Responsive Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* LEFT SIDE: Profile Preview */}
        <div className="space-y-3" data-tour="profile-preview">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900">Profile Preview</h3>
            <p className="text-xs sm:text-sm text-gray-500">How artists see your card</p>
          </div>
          
          {/* Mini Card Preview */}
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="w-full sm:w-48 flex-shrink-0">
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white">
                {/* Card Header Image */}
                <div className="relative h-28 sm:h-32 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                  {avatar ? (
                    <img
                      src={avatar}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      style={{ 
                        objectPosition: cardImagePosition === 'top' ? 'center top' : 
                                       cardImagePosition === 'bottom' ? 'center bottom' : 'center center'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[#F2D857] text-black text-3xl sm:text-4xl font-bold">
                      {getInitials(displayName || user?.name || '?')}
                    </div>
                  )}
                  {/* Badge */}
                  <div className="absolute top-1.5 right-1.5">
                    {isProfileComplete(user) ? (
                      <Badge className="bg-emerald-500 text-white text-[10px]">
                        <Check className="h-2 w-2 mr-0.5" />
                        Verified
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-500 text-white text-[10px]">
                        Onboarding
                      </Badge>
                    )}
                  </div>
                </div>
                {/* Card Body */}
                <div className="p-2 sm:p-3">
                  <h4 className="font-bold text-xs sm:text-sm truncate">{displayName || user?.name || 'Your Name'}</h4>
                  <p className="text-[10px] sm:text-xs text-gray-500 truncate">@{username || user?.username || 'username'}</p>
                  <p className="text-[10px] sm:text-xs text-gray-600 mt-1.5 line-clamp-2">
                    {bio || 'Your bio will appear here...'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Card Position Controls */}
            <div className="flex-1 w-full sm:w-auto flex flex-col">
              {/* Find Reviewers badge + Image Position header row */}
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs sm:text-sm font-medium text-gray-700">Image Position</p>
                <Badge variant="outline" className="text-[10px] sm:text-xs border-gray-300">
                  Find Reviewers
                </Badge>
              </div>
              
              {avatar && (
                <>
                  <div className="flex gap-1.5 sm:gap-2 mb-2">
                    {['top', 'center', 'bottom'].map((pos) => (
                      <Button
                        key={pos}
                        type="button"
                        variant={cardImagePosition === pos ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCardImagePosition(pos)}
                        className={`text-xs px-2 sm:px-3 py-1 ${cardImagePosition === pos ? 'bg-[#F2D857] text-black hover:bg-[#e5c94d]' : ''}`}
                      >
                        {pos.charAt(0).toUpperCase() + pos.slice(1)}
                      </Button>
                    ))}
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-500 mb-4">Focus your image on the card</p>
                </>
              )}
              
              {/* View Public Profile Button - parallel to Go Live */}
              <Button 
                size="sm"
                className="bg-gray-600 hover:bg-gray-700 text-white text-xs sm:text-sm w-full sm:w-auto"
                onClick={() => {
                  const currentUsername = username || user?.username;
                  if (currentUsername) {
                    window.open(`/reviewer/${currentUsername}`, '_blank');
                  }
                }}
                disabled={!username && !user?.username}
                data-testid="view-profile-btn"
              >
                <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5" />
                View Public Profile
              </Button>
            </div>
          </div>
        </div>
        
        {/* RIGHT SIDE: Visibility Boost */}
        <div className="space-y-4 lg:border-l lg:border-amber-200 lg:pl-6" data-tour="visibility-score">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Boost Your Visibility</h3>
                <p className="text-xs sm:text-sm text-gray-600">Climb the rankings</p>
              </div>
            </div>
            
            {/* Rank Badge */}
            {loading ? (
              <Badge className="bg-gray-300 text-white text-xs px-2 py-1">...</Badge>
            ) : rankData?.isVisible ? (
              <Badge className="bg-amber-500 text-white text-xs sm:text-sm px-2 sm:px-3 py-1" data-testid="rank-badge">
                #{rankData.rank} of {rankData.totalVisible}
              </Badge>
            ) : (
              <Badge className="bg-gray-400 text-white text-xs px-2 py-1" data-testid="rank-badge-hidden">
                <Eye className="w-3 h-3 mr-1" />
                Hidden
              </Badge>
            )}
          </div>
          
          {/* Activity Score */}
          <div className="bg-white rounded-lg p-3 border border-amber-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs sm:text-sm font-medium text-gray-600">Activity Score</span>
              <span className="text-xl sm:text-2xl font-bold text-amber-600" data-testid="activity-score">
                {loading ? '...' : (rankData?.activityScore || 0).toFixed(0)}
              </span>
            </div>
            <Progress 
              value={Math.min(((rankData?.activityScore || 0) / 200) * 100, 100)} 
              className="h-1.5 sm:h-2 bg-amber-100"
            />
            <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
              Higher score = higher ranking
            </p>
          </div>
          
          {/* Next Action CTA */}
          <div 
            className={`rounded-lg p-3 ${
              milestone.priority === 'high' 
                ? 'bg-orange-100 border border-orange-200' 
                : 'bg-amber-100 border border-amber-200'
            }`}
            data-testid="next-milestone"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Zap className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${milestone.priority === 'high' ? 'text-orange-600' : 'text-amber-600'}`} />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{milestone.action}</p>
                  <p className="text-xs sm:text-sm text-gray-600">{milestone.impact}</p>
                </div>
              </div>
              {onGoLive && (
                <Button 
                  onClick={onGoLive}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm w-full sm:w-auto"
                  data-testid="milestone-start-session-btn"
                >
                  Start Session
                </Button>
              )}
            </div>
          </div>
          
          {/* Expandable Score Breakdown */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors py-1"
            data-testid="expand-breakdown"
          >
            <span className="flex items-center gap-1">
              <Info className="w-3 h-3 sm:w-4 sm:h-4" />
              How is my score calculated?
            </span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {expanded && (
            <div className="pt-3 border-t border-amber-200 space-y-2" data-testid="score-breakdown">
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="flex items-center gap-1.5 text-gray-600">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  Active Minutes
                </span>
                <span className="font-medium">{rankData?.totalActiveMinutes?.toFixed(0) || 0} × 1 = <span className="text-amber-600">{scoreBreakdown.minutes}</span></span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="flex items-center gap-1.5 text-gray-600">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  Live Days
                </span>
                <span className="font-medium">{rankData?.uniqueLiveDays || 0} × 20 = <span className="text-amber-600">{scoreBreakdown.days}</span></span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="flex items-center gap-1.5 text-gray-600">
                  <Star className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  Reviews
                </span>
                <span className="font-medium">{rankData?.totalReviews || 0} × 5 = <span className="text-amber-600">{scoreBreakdown.reviews}</span></span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-amber-100 text-xs sm:text-sm">
                <span className="font-medium text-gray-700">Total</span>
                <span className="font-bold text-amber-600">{(rankData?.activityScore || 0).toFixed(0)} pts</span>
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 bg-amber-50 p-2 rounded mt-2">
                <strong>Tip:</strong> {getContextualTip(rankData)}
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default VisibilityAndPreviewCard;
